// src/pages/staff-management/components/StaffForm.jsx
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";

const DEFAULT_WORKING_HOURS = {
  monday: {
    enabled: true,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  tuesday: {
    enabled: true,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  wednesday: {
    enabled: true,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  thursday: {
    enabled: true,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  friday: {
    enabled: true,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  saturday: {
    enabled: false,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
  sunday: {
    enabled: false,
    start: "09:00",
    end: "17:00",
    break: { start: "12:00", end: "13:00" },
  },
};

// normalizează orice vine din backend (id-uri, stringuri, obiecte) într-o listă de ID-uri
const normalizeServiceIds = (raw) => {
  if (!raw) return [];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => {
      if (typeof item === "number") return item;
      if (typeof item === "string") {
        const n = parseInt(item, 10);
        return Number.isNaN(n) ? null : n;
      }
      if (item && typeof item === "object" && "id" in item) return item.id;
      return null;
    })
    .filter((id) => Number.isInteger(id));
};

// dacă working_hours din backend este {} sau null -> folosim DEFAULT_WORKING_HOURS
const getInitialWorkingHours = (staff) => {
  const candidate =
    staff?.workingHours ||
    staff?.working_hours ||
    null;

  if (
    candidate &&
    typeof candidate === "object" &&
    Object.keys(candidate).length > 0
  ) {
    return candidate;
  }

  // fallback: default standard
  return {
    ...DEFAULT_WORKING_HOURS,
  };
};

const StaffForm = ({
  staff,
  onSave,
  onCancel,
  isEditing = false,
  serviceOptions = [],
  categoryOptions = [],
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name:
      staff?.name ||
      `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim(),
    email: staff?.email || "",
    phone: staff?.phone || "",
    username: staff?.username || staff?._raw?.username || "",
    password: "",
    // role = cod backend: "director" | "reception" | "employee" | "owner"
    role: staff?.role || staff?.roles?.business || "employee",
    status:
      staff?.status || (staff?.is_active === false ? "inactive" : "active"),
    // ⚠️ acum servicii = listă de ID-uri, nu de nume
    services: normalizeServiceIds(staff?.services || staff?.service_ids),
    // program de lucru vine din backend (JSON) sau default, dar tratăm și cazul {} din DB
    workingHours: getInitialWorkingHours(staff),
  });

  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [showWorkingHours, setShowWorkingHours] = useState(false);

  const [showPasswordReset, setShowPasswordReset] = useState(!isEditing);

  // fișier avatar + preview (fără imagine default – doar placeholder)
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(
    staff?.avatar || staff?.avatar_url || ""
  );

  // Filtru categorie (UI-only) pentru lista de servicii
  const [categoryFilter, setCategoryFilter] = useState(() => {
    const raw =
      staff?.serviceCategoryFilters ||
      staff?.service_category_filters ||
      staff?._raw?.service_category_filters ||
      [];
    return Array.isArray(raw) ? raw.filter(Boolean) : [];
  });

  const filteredServiceOptions = useMemo(() => {
    const all = Array.isArray(serviceOptions) ? serviceOptions : [];
    const selectedIds = new Set(formData?.services || []);

    const picked = Array.isArray(categoryFilter) ? categoryFilter.filter(Boolean) : [];
    if (picked.length === 0) return all;

    return all.filter((s) => {
      if (selectedIds.has(s?.id)) return true; // nu ascundem serviciile deja selectate
      const raw = (s?.category || "").toString().trim();
      const key = raw ? raw : "__none__";
      return picked.includes(key);
    });
  }, [serviceOptions, categoryFilter, formData?.services]);

  const roleOptions = [
    { value: "director", label: t("staff.roleManager") },
    { value: "employee", label: t("staff.roleEmployee") },
    { value: "reception", label: t("staff.roleReception") },
    { value: "owner", label: t("staff.roleOwner") },
  ];

  const statusOptions = [
    { value: "active", label: t("staff.statusActive") },
    { value: "inactive", label: t("staff.statusInactive") },
    { value: "on_leave", label: t("staff.statusOnLeave") },
  ];

  const dayNames = {
    monday: t("staff.dayNames.monday"),
    tuesday: t("staff.dayNames.tuesday"),
    wednesday: t("staff.dayNames.wednesday"),
    thursday: t("staff.dayNames.thursday"),
    friday: t("staff.dayNames.friday"),
    saturday: t("staff.dayNames.saturday"),
    sunday: t("staff.dayNames.sunday"),
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  // acum lucrăm cu ID-ul serviciului
  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => {
      const current = prev?.services || [];
      const exists = current.includes(serviceId);
      return {
        ...prev,
        services: exists
          ? current.filter((id) => id !== serviceId)
          : [...current, serviceId],
      };
    });

    if (errors?.services) {
      setErrors((prev) => ({ ...prev, services: null }));
    }
  };

  const handleWorkingHourChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: {
        ...prev?.workingHours,
        [day]: {
          ...prev?.workingHours?.[day],
          [field]: value,
        },
      },
    }));
  };

  const handleBreakTimeChange = (day, field, value) => {
    setFormData((prev) => ({
      ...prev,
      workingHours: {
        ...prev?.workingHours,
        [day]: {
          ...prev?.workingHours?.[day],
          break: {
            ...prev?.workingHours?.[day]?.break,
            [field]: value,
          },
        },
      },
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    try {
      const url = URL.createObjectURL(file);
      setAvatarPreview(url);
    } catch {
      // ignorăm dacă nu merge createObjectURL
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = t("staff.nameRequired");
    }

    if (!formData?.email?.trim()) {
      newErrors.email = t("staff.emailRequired");
    } else if (!/\S+@\S+\.\S+/.test(formData?.email)) {
      newErrors.email = t("staff.emailInvalid");
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = t("staff.phoneRequired");
    }

    if (!isEditing) {
      if (!formData?.username?.trim()) {
        newErrors.username = t("staff.usernameRequired");
      }
      if (!formData?.password?.trim()) {
        newErrors.password = t("staff.passwordRequired");
      }
    }

    if (!isEditing && (!formData?.services || formData?.services?.length === 0)) {
      newErrors.services = t("staff.noServicesSelected");
    }

    setErrors(newErrors);

    // setăm și mesajul global, dacă e cazul
    const keys = Object.keys(newErrors);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const firstMsg = newErrors[firstKey];
      setGlobalError(t("staff.saveError", { msg: firstMsg }));
    } else {
      setGlobalError("");
    }

    return keys.length === 0;
  };

  const categorySelectOptions = useMemo(() => {
    const optsFromApi = Array.isArray(categoryOptions)
      ? categoryOptions
          .map((c) => ({
            value: (c?.name || "").toString().trim(),
            label: (c?.name || "").toString().trim(),
          }))
          .filter((x) => x.value && x.label)
      : [];

    const optsFromServices = Array.isArray(serviceOptions)
      ? Array.from(
          new Set(
            serviceOptions
              .map((s) => (s?.category || "").toString().trim())
              .filter(Boolean)
          )
        ).map((name) => ({ value: name, label: name }))
      : [];

    const merged = [...optsFromApi, ...optsFromServices]
      // dedup by value
      .reduce((acc, cur) => {
        if (!acc.some((x) => x.value === cur.value)) acc.push(cur);
        return acc;
      }, []);

    // "fără categorie" ca opțiune utilă
    const hasNoCategory = (serviceOptions || []).some(
      (s) => !(s?.category || "").toString().trim()
    );
    if (hasNoCategory) merged.unshift({ value: "__none__", label: t("staff.noCategory") });

    return merged;
  }, [categoryOptions, serviceOptions]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    const isValid = validateForm();
    if (!isValid) return;

    // trimitem spre parent TOT ce trebuie pentru backend:
    //  - avatarFile -> <input type="file">
    //  - workingHours -> JSON
    //  - services -> listă de ID-uri (int)
    const payload = {
      ...formData,
      id: staff?.id || Date.now(),
      avatar: avatarPreview || "", // doar pentru preview în UI
      avatarFile: avatarFile || null,
      services: formData?.services || [],
      service_category_filters: Array.isArray(categoryFilter) ? categoryFilter : [],
    };

    // la edit nu trimitem parola dacă nu e resetată
    if (isEditing && !showPasswordReset) {
      delete payload.password;
    }
    if (isEditing && showPasswordReset && !payload.password) {
      // nu trimite parola goală
      delete payload.password;
    }

    onSave(payload);
  };

  // dacă dintr-un motiv oarecare workingHours ajunge iar {}, facem fallback și la randare
  const workingHoursForRender =
    formData?.workingHours &&
    Object.keys(formData.workingHours).length > 0
      ? formData.workingHours
      : DEFAULT_WORKING_HOURS;

  return (
    <div className="bg-background rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          {isEditing ? t("staff.editStaff") : t("staff.newStaff")}
        </h2>
        <Button variant="ghost" size="icon" onClick={onCancel}>
          <Icon name="X" size={20} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info + poză angajat */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-6 items-start">
          {/* Date de bază */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t("staff.fullName")}
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange("name", e?.target?.value)}
                error={errors?.name}
                required
                placeholder={t("staff.fullNamePlaceholder")}
              />

              <Input
                label="Username"
                type="text"
                value={formData?.username}
                onChange={(e) => handleInputChange("username", e?.target?.value)}
                error={errors?.username}
                required={!isEditing}
                disabled={isEditing}
                placeholder={t("staff.usernamePlaceholder")}
              />

              <Input
                label="Email"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange("email", e?.target?.value)}
                error={errors?.email}
                required
                placeholder={t("staff.emailPlaceholder")}
              />

              {!isEditing ? (
                <Input
                  label={t("staff.password")}
                  type="password"
                  value={formData?.password}
                  onChange={(e) => handleInputChange("password", e?.target?.value)}
                  error={errors?.password}
                  required
                  placeholder={t("staff.passwordPlaceholder")}
                />
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-foreground">
                      {t("staff.password")}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowPasswordReset((v) => {
                          if (v) {
                            setFormData((prev) => ({ ...prev, password: "" }));
                          }
                          return !v;
                        });
                      }}
                    >
                      {showPasswordReset ? t("staff.cancelPassword") : t("staff.resetPassword")}
                    </Button>
                  </div>

                  {showPasswordReset && (
                    <Input
                      label={t("staff.newPassword")}
                      type="password"
                      value={formData?.password}
                      onChange={(e) => handleInputChange("password", e?.target?.value)}
                      error={errors?.password}
                      required={false}
                      placeholder={t("staff.passwordLeaveBlank")}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label={t("common.phone")}
                type="tel"
                value={formData?.phone}
                onChange={(e) => handleInputChange("phone", e?.target?.value)}
                error={errors?.phone}
                required
                placeholder="+40 123 456 789"
              />

              <Select
                label={t("staff.role")}
                options={roleOptions}
                value={formData?.role}
                onChange={(value) => handleInputChange("role", value)}
                required
              />

              <Select
                label={t("common.status")}
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange("status", value)}
                required
              />
            </div>
          </div>

          {/* Poză angajat: avatar + buton dedesubt */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border border-border flex items-center justify-center">
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt={formData?.name || "Avatar angajat"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={32} className="text-text-secondary" />
              )}
            </div>

            <label className="inline-flex items-center px-3 py-2 border border-border rounded-md text-sm cursor-pointer bg-muted hover:bg-muted/80 transition-colors">
              <Icon name="Upload" size={16} className="mr-2" />
              <span>{t("staff.uploadPhoto")}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </label>

            {avatarFile && (
              <span className="text-xs text-text-secondary truncate max-w-[160px]">
                {avatarFile.name}
              </span>
            )}
          </div>
        </div>

        {/* Categorii (filtru UI) + Servicii (pe ID) */}
        <div>
          <Select
            label={t("staff.categoryFilterLabel")}
            options={categorySelectOptions}
            value={categoryFilter}
            onChange={(v) => {
              const next = Array.isArray(v) ? v : v ? [v] : [];
              setCategoryFilter(next);

              // daca exista filtru, eliminam automat serviciile selectate care nu mai apartin filtrului
              setFormData((prev) => {
                if (!next || next.length === 0) return prev;
                const allowed = new Set(
                  (serviceOptions || [])
                    .filter((s) => {
                      const raw = (s?.category || "").toString().trim();
                      const key = raw ? raw : "__none__";
                      return next.includes(key);
                    })
                    .map((s) => s.id)
                );
                const current = prev?.services || [];
                const filtered = current.filter((id) => allowed.has(id));
                return { ...prev, services: filtered };
              });
            }}
            multiple
            searchable
            placeholder={t("staff.allCategoriesPlaceholder")}
          />
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground mb-3">
              {t("staff.services")} <span className="text-error">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {filteredServiceOptions?.map((service) => (
                <Checkbox
                  key={service?.id}
                  label={service?.name || "-"}
                  checked={formData?.services?.includes(service?.id)}
                  onChange={() => handleServiceToggle(service?.id)}
                />
              ))}
            </div>
            {errors?.services && (
              <p className="text-sm text-error mt-1">{errors?.services}</p>
            )}
          </div>
        </div>

        {/* Program de lucru – ASCUNS implicit, se deschide la click */}
        <div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
            <h3 className="text-lg font-medium text-foreground">
              {t("staff.workingHoursTitle")}
            </h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowWorkingHours((v) => !v)}
            >
              <Icon
                name={showWorkingHours ? "ChevronUp" : "ChevronDown"}
                size={16}
                className="mr-2"
              />
              {showWorkingHours
                ? t("staff.hideSchedule")
                : t("staff.configureSchedule")}
            </Button>
          </div>
          <p className="text-xs text-text-secondary mb-3">
            {t("staff.workingHoursNote")}
          </p>

          {showWorkingHours && (
            <div className="space-y-4">
              {Object.entries(workingHoursForRender)?.map(([day, hours]) => (
                <div key={day} className="bg-muted rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <Checkbox
                      label={dayNames?.[day] || day}
                      checked={!!hours?.enabled}
                      onChange={(e) =>
                        handleWorkingHourChange(
                          day,
                          "enabled",
                          e?.target?.checked
                        )
                      }
                    />
                  </div>

                  {hours?.enabled && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Input
                        label={t("staff.workStart")}
                        type="time"
                        value={hours?.start || ""}
                        onChange={(e) =>
                          handleWorkingHourChange(
                            day,
                            "start",
                            e?.target?.value
                          )
                        }
                      />

                      <Input
                        label={t("staff.workEnd")}
                        type="time"
                        value={hours?.end || ""}
                        onChange={(e) =>
                          handleWorkingHourChange(
                            day,
                            "end",
                            e?.target?.value
                          )
                        }
                      />

                      <Input
                        label={t("staff.breakStart")}
                        type="time"
                        value={hours?.break?.start || ""}
                        onChange={(e) =>
                          handleBreakTimeChange(
                            day,
                            "start",
                            e?.target?.value
                          )
                        }
                      />

                      <Input
                        label={t("staff.breakEnd")}
                        type="time"
                        value={hours?.break?.end || ""}
                        onChange={(e) =>
                          handleBreakTimeChange(
                            day,
                            "end",
                            e?.target?.value
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mesaj global de eroare de validare */}
        {globalError && (
          <div className="text-sm text-error bg-error/10 border border-error/40 rounded-md px-3 py-2">
            {globalError}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
          <Button variant="outline" onClick={onCancel} type="button">
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="default">
            {isEditing ? t("common.update") : t("common.save2")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default StaffForm;
